import { useEffect, useRef, type ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Sheet title rendered in the header; required for a11y. */
  title: ReactNode;
  /** Optional description rendered under the title. */
  description?: ReactNode;
  /** Body content. */
  children: ReactNode;
  /** Sticky footer (action buttons). Optional. */
  footer?: ReactNode;
  /**
   * Extra className appended to the sheet content element so callers can
   * tune sizing without overriding the base styles.
   */
  className?: string;
  testId?: string;
}

/**
 * Bottom-sheet primitive for the mobile moderation UI.
 *
 * Built on Radix Dialog so we inherit the focus trap, Escape close, and
 * scrim semantics that the rest of the app already uses. The visual
 * presentation is a sheet that slides up from the bottom of the viewport
 * with a drag handle, a header, a scrollable body, and an optional
 * sticky footer that sits above the iOS safe-area inset.
 *
 * The drag-down-to-dismiss gesture is handled with a small custom touch
 * listener on the handle: lifting the sheet past 80 px closes it. The
 * panel itself remains pointer-driven so the user can still scroll the
 * body content with their thumb.
 */
export default function BottomSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
  testId,
}: BottomSheetProps) {
  const dragStartY = useRef<number | null>(null);
  const dragDelta = useRef<number>(0);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Reset drag transform whenever the sheet is mounted or remounted so
  // we don't carry over a stale `translateY` from a previous close.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    panel.style.transform = '';
  }, [open]);

  const onHandleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0];
    if (!touch) return;
    dragStartY.current = touch.clientY;
    dragDelta.current = 0;
  };

  const onHandleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (dragStartY.current === null) return;
    const touch = e.touches[0];
    if (!touch) return;
    const delta = Math.max(0, touch.clientY - dragStartY.current);
    dragDelta.current = delta;
    const panel = panelRef.current;
    if (panel) {
      panel.style.transform = `translateY(${delta}px)`;
      panel.style.transition = 'none';
    }
  };

  const onHandleTouchEnd = () => {
    const panel = panelRef.current;
    const delta = dragDelta.current;
    dragStartY.current = null;
    dragDelta.current = 0;
    if (!panel) return;
    panel.style.transition = 'transform 200ms ease';
    if (delta > 80) {
      // Animate out and call onOpenChange so Radix unmounts the dialog.
      panel.style.transform = 'translateY(100%)';
      window.setTimeout(() => onOpenChange(false), 180);
    } else {
      panel.style.transform = '';
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="bottom-sheet__overlay"
          data-testid={testId ? `${testId}-overlay` : 'bottom-sheet-overlay'}
        />
        <DialogPrimitive.Content
          ref={panelRef}
          className={`bottom-sheet__panel${className ? ` ${className}` : ''}`}
          data-testid={testId ?? 'bottom-sheet'}
          aria-describedby={description ? undefined : undefined}
        >
          <div
            className="bottom-sheet__handle-area"
            onTouchStart={onHandleTouchStart}
            onTouchMove={onHandleTouchMove}
            onTouchEnd={onHandleTouchEnd}
            onTouchCancel={onHandleTouchEnd}
            aria-hidden="true"
          >
            <div className="bottom-sheet__handle" />
          </div>
          <header className="bottom-sheet__header">
            <DialogPrimitive.Title className="bottom-sheet__title">
              {title}
            </DialogPrimitive.Title>
            {description && (
              <DialogPrimitive.Description className="bottom-sheet__desc">
                {description}
              </DialogPrimitive.Description>
            )}
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                className="bottom-sheet__close"
                aria-label="Close"
                data-testid={
                  testId ? `${testId}-close` : 'bottom-sheet-close'
                }
              >
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                  <path
                    d="M4 4l10 10M14 4L4 14"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </DialogPrimitive.Close>
          </header>
          <div className="bottom-sheet__body" data-testid="bottom-sheet-body">
            {children}
          </div>
          {footer && <div className="bottom-sheet__footer">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
